import { ApiService } from "@/services/api/ApiService";
import { ensureTextContrast } from "@/lib/utils";
import {
  EmailTemplateConfig,
  EmailTemplateType,
  EmailTemplateWithAgent,
  GoogleFont,
} from "@/types/email-templates";

import type { GenieBranding } from "@/types/ui/branding";
import { fetchWithAuth } from "@/services/auth/fetchWithAuth";

class EmailTemplateService {
  /**
   * Get template config with fallback: agent-specific → user-level → default
   */
  async getTemplateConfig(
    userId: string,
    type: EmailTemplateType,
    agentId?: string | null,
  ): Promise<EmailTemplateConfig | null> {
    const result = await ApiService.invoke<{ config: EmailTemplateConfig | null }>({
      resource: "email-templates",
      action: "getConfig",
      data: {
        userId,
        type,
        agent_id: agentId,
      },
    });

    return result?.config || null;
  }

  async saveTemplateConfig(
    userId: string,
    type: EmailTemplateType,
    config: EmailTemplateConfig,
    agentId?: string | null,
  ): Promise<number> {
    const result = await ApiService.invoke<{ id: number }>({
      resource: "email-templates",
      action: "update",
      data: {
        userId,
        type,
        config,
        agent_id: agentId,
      },
    });

    return result?.id || 0;
  }

  async sendConversationReport(params: {
    recipients: string[];
    conversation: any;
    aiResult: any;
    topics: any[];
    insights: string[];
    transcript: any[];
    userId: string;
    agentId?: string;
    redirect_url?: string;
  }): Promise<any> {
    const response = await fetchWithAuth(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/email-handler`,
      {
        method: "POST",
        body: JSON.stringify({
          action: "conversationReport",
          ...params,
        }),
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to send conversation report: ${response.statusText}`);
    }

    return await response.json();
  }

  async sendCustomerReport(params: {
    recipients: string[];
    conversation: any;
    aiResult: any;
    userId: string;
    agentId?: string;
  }): Promise<any> {
    const response = await fetchWithAuth(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/email-handler`,
      {
        method: "POST",
        body: JSON.stringify({
          action: "customerReport",
          ...params,
        }),
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to send customer report: ${response.statusText}`);
    }

    return await response.json();
  }

  async listTemplates(userId: string, type?: EmailTemplateType): Promise<EmailTemplateWithAgent[]> {
    const result = await ApiService.invoke<{ items: EmailTemplateWithAgent[] }>({
      resource: "email-templates",
      action: "list",
      data: {
        userId,
        type,
        limit: 500,
      },
    });

    return result?.items || [];
  }

  async cloneDefaultTemplate(
    userId: string,
    type: EmailTemplateType,
    agentId?: string | null,
  ): Promise<EmailTemplateConfig> {
    const result = await ApiService.invoke<{ config: EmailTemplateConfig }>({
      resource: "email-templates",
      action: "cloneDefault",
      data: {
        userId,
        type,
        agent_id: agentId,
      },
    });

    return result?.config || {};
  }

  /**
   * Flips the "Email Signature" block's enabled flag across all 6 report
   * templates for one genie, after BrandingModal saves a signatureStyle.enabled
   * change. Only affects this genie's own templates — forks an agent-specific
   * row from its current effective config (agent-specific → user-level →
   * default) when it doesn't have one yet, so other genies sharing a user-level
   * template are never silently affected.
   */
  async syncSignatureEnabled(agentId: string, enabled: boolean): Promise<void> {
    await ApiService.invoke({
      resource: "email-templates",
      action: "sync-signature-enabled",
      data: {
        agent_id: agentId,
        enabled,
      },
    });
  }

  private async copyTeamLogoForEmailTemplate(logoUrl: string | undefined): Promise<string | undefined> {
    if (!logoUrl) return undefined;

    if (logoUrl.includes("genie-images") || !logoUrl.includes("supabase.co")) {
      return logoUrl;
    }

    try {
      const urlParts = logoUrl.split("/storage/v1/object/public/");
      if (urlParts.length !== 2) {
        return logoUrl;
      }

      const result = await ApiService.invoke<{ logoUrl: string }>({
        resource: "email-templates",
        action: "copyLogo",
        data: { logoUrl },
      });

      return result?.logoUrl || logoUrl;
    } catch (error) {
      console.warn("Error copying logo for email template:", error);
      return logoUrl;
    }
  }

  async applyTeamBranding(
    teamBranding: GenieBranding,
    baseConfig?: EmailTemplateConfig,
  ): Promise<{ config: EmailTemplateConfig; hasBranding: boolean }> {
    const VALID_GOOGLE_FONTS = ["Poppins", "Roboto", "Inter", "Lato", "Open Sans"] as const;

    const validateGoogleFont = (fontFamily?: string): GoogleFont | undefined => {
      if (!fontFamily) return undefined;
      const trimmed = fontFamily.trim();
      if (VALID_GOOGLE_FONTS.includes(trimmed as any)) {
        return trimmed as GoogleFont;
      }
      return undefined;
    };

    const hasColorBranding =
      teamBranding?.primaryColor ||
      teamBranding?.secondaryColor ||
      teamBranding?.textColor ||
      teamBranding?.backgroundColor;
    const hasLogoBranding = teamBranding?.logoUrl;
    const hasEmbedStyle =
      teamBranding?.embedStyle &&
      (teamBranding.embedStyle.fontFamily ||
        teamBranding.embedStyle.buttonStyle ||
        teamBranding.embedStyle.headerColor ||
        teamBranding.embedStyle.borderRadius !== undefined ||
        teamBranding.embedStyle.shadowEnabled !== undefined);

    if (!teamBranding || (!hasColorBranding && !hasLogoBranding && !hasEmbedStyle)) {
      return { config: baseConfig || {}, hasBranding: false };
    }

    const validatedGoogleFont = validateGoogleFont(teamBranding.embedStyle?.fontFamily);

    const bgColor = teamBranding.backgroundColor || baseConfig?.colors?.background || "#FFFFFF";
    const textColor = teamBranding.textColor || baseConfig?.colors?.text;
    const validatedTextColor = textColor ? ensureTextContrast(textColor, bgColor) : textColor;

    const copiedLogoUrl = await this.copyTeamLogoForEmailTemplate(teamBranding.logoUrl);

    return {
      config: {
        ...baseConfig,
        colors: {
          primary: teamBranding.primaryColor || baseConfig?.colors?.primary,
          accent: teamBranding.secondaryColor || baseConfig?.colors?.accent,
          text: validatedTextColor || baseConfig?.colors?.text,
          background: bgColor,
          headerColor: teamBranding.embedStyle?.headerColor || baseConfig?.colors?.headerColor,
        },
        fonts: {
          ...baseConfig?.fonts,
          googleFont: validatedGoogleFont || baseConfig?.fonts?.googleFont,
        },
        branding: {
          logoUrl: copiedLogoUrl || baseConfig?.branding?.logoUrl,
          companyName: baseConfig?.branding?.companyName,
        },
        buttons: {
          buttonStyle: teamBranding.embedStyle?.buttonStyle || baseConfig?.buttons?.buttonStyle,
          borderRadius: teamBranding.embedStyle?.borderRadius ?? baseConfig?.buttons?.borderRadius,
          shadowEnabled: teamBranding.embedStyle?.shadowEnabled ?? baseConfig?.buttons?.shadowEnabled,
        },
      },
      hasBranding: true,
    };
  }

  async applyAgentBranding(
    agentBranding: GenieBranding,
    agentName: string,
    baseConfig?: EmailTemplateConfig,
  ): Promise<{ config: EmailTemplateConfig; hasBranding: boolean }> {
    const VALID_GOOGLE_FONTS = ["Poppins", "Roboto", "Inter", "Lato", "Open Sans"] as const;

    const validateGoogleFont = (fontFamily?: string): GoogleFont | undefined => {
      if (!fontFamily) return undefined;
      const trimmed = fontFamily.trim();
      if (VALID_GOOGLE_FONTS.includes(trimmed as any)) {
        return trimmed as GoogleFont;
      }
      return undefined;
    };

    const hasColorBranding =
      agentBranding?.primaryColor ||
      agentBranding?.secondaryColor ||
      agentBranding?.textColor ||
      agentBranding?.backgroundColor;
    const hasLogoBranding = agentBranding?.logoUrl;
    const hasEmbedStyle =
      agentBranding?.embedStyle &&
      (agentBranding.embedStyle.fontFamily ||
        agentBranding.embedStyle.buttonStyle ||
        agentBranding.embedStyle.headerColor ||
        agentBranding.embedStyle.borderRadius !== undefined ||
        agentBranding.embedStyle.shadowEnabled !== undefined);

    if (!agentBranding || (!hasColorBranding && !hasLogoBranding && !hasEmbedStyle)) {
      return { config: baseConfig || {}, hasBranding: false };
    }

    const validatedGoogleFont = validateGoogleFont(agentBranding.embedStyle?.fontFamily);

    const bgColor = agentBranding.backgroundColor || baseConfig?.colors?.background || "#FFFFFF";
    const textColor = agentBranding.textColor || baseConfig?.colors?.text;
    const validatedTextColor = textColor ? ensureTextContrast(textColor, bgColor) : textColor;

    return {
      config: {
        ...baseConfig,
        colors: {
          primary: agentBranding.primaryColor || baseConfig?.colors?.primary,
          accent: agentBranding.secondaryColor || baseConfig?.colors?.accent,
          text: validatedTextColor || baseConfig?.colors?.text,
          background: bgColor,
          // Cascade through the tenant's text color when no explicit
          // embedStyle.headerColor is configured, so monochrome brands
          // (e.g. all-black NZ Funds Advisor) don't inherit HG's navy
          // default from the base template's headerColor.
          headerColor:
            agentBranding.embedStyle?.headerColor ||
            validatedTextColor ||
            baseConfig?.colors?.headerColor,
        },
        fonts: {
          ...baseConfig?.fonts,
          googleFont: validatedGoogleFont || baseConfig?.fonts?.googleFont,
        },
        branding: {
          logoUrl: agentBranding.logoUrl || baseConfig?.branding?.logoUrl,
          companyName: agentName || baseConfig?.branding?.companyName,
        },
        buttons: {
          buttonStyle: agentBranding.embedStyle?.buttonStyle || baseConfig?.buttons?.buttonStyle,
          borderRadius: agentBranding.embedStyle?.borderRadius ?? baseConfig?.buttons?.borderRadius,
          shadowEnabled: agentBranding.embedStyle?.shadowEnabled ?? baseConfig?.buttons?.shadowEnabled,
        },
      },
      hasBranding: true,
    };
  }

  async deleteTemplate(templateId: number): Promise<void> {
    await ApiService.invoke({
      resource: "email-templates",
      action: "delete",
      id: templateId.toString(),
    });
  }
}

export const emailTemplateService = new EmailTemplateService();
